import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import type { RowSelectionState, SortingState } from '@tanstack/react-table';
import {
  ArrowDownUp,
  ArrowUpRight,
  Columns3,
  Ellipsis,
  LayoutGrid,
  Plus,
  RotateCcw,
  Search,
  Send,
  Trash2,
  CloudOff,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';

import type { EnvKey } from '@/app/site/scope';
import { useSiteScope } from '@/app/site/useSiteScope';
import { PromoteDialog } from '@/features/transfer/PromoteDialog';
import { contentTypesGet } from '@/shared/api/generated/content/content';
import type {
  FieldFilter,
  RecordBody,
  RecordsListParams,
  ViewBody,
  ViewConfig,
} from '@/shared/api/generated/model';
import {
  recordsBulk,
  recordsDelete,
  recordsList,
  recordsRestore,
  recordsViewCreate,
  recordsViewDelete,
  recordsViewUpdate,
  recordsViews,
} from '@/shared/api/generated/records/records';
import { ApiError } from '@/shared/api/http';
import { pluralize } from '@/shared/lib/plural';
import { useDebounced } from '@/shared/lib/useDebounced';
import { AuthImage } from '@/shared/ui/AuthImage';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Checkbox } from '@/shared/ui/Checkbox';
import {
  DataTable,
  DataTableEmpty,
  DataTablePagination,
  DataTableSelection,
  dataTableColumns,
  useDataTable,
} from '@/shared/ui/DataTable';
import { ConfirmDialog, Dialog, DialogContent } from '@/shared/ui/Dialog';
import { Field } from '@/shared/ui/Field';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/shared/ui/DropdownMenu';
import { Input } from '@/shared/ui/Input';
import { Segmented } from '@/shared/ui/Segmented';
import { toast } from '@/shared/ui/Toast';

import { Kanban } from '@/features/stages/Kanban';
import { hasStages } from '@/features/stages/stages';

import styles from './ContentPage.module.scss';
import { FiltersPanel, FiltersSide } from './FiltersPanel';
import {
  DEFAULT_CONFIG,
  cellText,
  listFields,
  sameConfig,
  selectRange,
  sortable,
} from './listModel';
import { NewRecordDialog } from './NewRecordDialog';
import { OrderList } from './OrderList';

const NO_RECORDS: RecordBody[] = [];

const helper = dataTableColumns<RecordBody>();

const dateFormat = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

type BulkAction = 'publish' | 'unpublish' | 'delete' | 'restore';

const BULK_DONE: Record<BulkAction, string> = {
  publish: 'Опубликовано',
  unpublish: 'Снято с сайта',
  delete: 'В корзине',
  restore: 'Возвращено',
};

/**
 * Записи одной коллекции в текущем окружении.
 *
 * Коллекция и окружение — в адресе: их выбирают меню и переключатель
 * окружения в оболочке, а не фильтры на экране. На экране — как показывать
 * (таблица или карточки, столбцы) и что отбирать (поиск, состояние, условия
 * по полям); удачную настройку можно сохранить видом для всей команды.
 */
export function ContentPage() {
  const { env, typeId } = useParams({ from: '/authed/sites/$siteId/$env/c/$typeId' });

  // Другая коллекция или окружение — новый экран: поиск, вид и выбор от
  // прежнего списка к новому не относятся.
  return <CollectionList key={`${typeId}:${env}`} typeId={typeId} />;
}

/**
 * Записи модели — вкладка «Записи» её деталки (embedded: заголовок и
 * описание показывает сама деталка).
 */
export function CollectionList({
  typeId,
  embedded = false,
}: {
  typeId: string;
  embedded?: boolean;
}) {
  const { siteId, env } = useParams({ from: '/authed/sites/$siteId/$env' });
  const scope = useSiteScope();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [deleted, setDeleted] = useState(false);
  const [creating, setCreating] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
  const [selection, setSelection] = useState<RowSelectionState>({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [promoting, setPromoting] = useState<string[]>([]);
  const [naming, setNaming] = useState(false);
  // Ручной порядок — отдельный режим: перетаскивание в таблице со
  // страницами и сортировками запутало бы, что куда встало.
  const [ordering, setOrdering] = useState(false);

  // Вид: выбранный сохранённый (или «Все записи») и настройка на экране —
  // она может уйти вперёд сохранённой, тогда предлагается сохранить.
  const [viewID, setViewID] = useState<string>();
  // Как показывать записи модели — таблицей, карточками, канбаном —
  // запоминается в браузере: товары удобнее карточками, заявки — таблицей.
  const [layout, setLayout] = useState<ViewConfig['layout']>(() => storedLayout(typeId));
  const base: ViewConfig = { ...DEFAULT_CONFIG, layout };
  const [config, setConfig] = useState<ViewConfig>(base);

  const debouncedSearch = useDebounced(search);
  const debouncedFilters = useDebounced(JSON.stringify(completeFilters(config.filters ?? [])));

  const currentType = scope?.types.find((item) => item.id === typeId);
  // Кнопки — только тем, кому сервер их действие разрешит (матрица ролей).
  const canWrite = scope?.can('content.write') ?? false;
  const canPublish = scope?.can('content.publish') ?? false;
  const currentEnv = scope?.environment;
  // У коллекции с ручным порядком список по умолчанию — в нём, как на сайте.
  const manual = currentType?.manual_order === true;
  const sortBy = config.sort ?? (manual ? 'position' : 'updated_at');
  const sortOrder = config.order ?? (manual ? 'asc' : 'desc');

  const schema = useQuery({
    queryKey: ['content-type', typeId],
    queryFn: async () => {
      const response = await contentTypesGet(typeId);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос типа');
      }

      return response.data;
    },
  });

  const views = useQuery({
    queryKey: ['views', typeId],
    queryFn: async () => {
      const response = await recordsViews(typeId);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос видов');
      }

      return response.data.items ?? [];
    },
  });

  const fields = listFields(schema.data?.fields ?? []);
  const currentView = views.data?.find((item) => item.id === viewID);
  const changed = !sameConfig(config, currentView?.config ?? base);

  const sorting: SortingState = [{ id: sortBy, desc: sortOrder === 'desc' }];

  const params = useMemo<RecordsListParams>(
    () => ({
      search: debouncedSearch,
      deleted,
      sort: sortBy,
      order: sortOrder,
      page: pagination.pageIndex + 1,
      per_page: pagination.pageSize,
      ...(config.status === undefined ? {} : { status: config.status }),
      ...(debouncedFilters === '[]' ? {} : { filters: debouncedFilters }),
    }),
    [debouncedSearch, deleted, sortBy, sortOrder, config.status, debouncedFilters, pagination],
  );

  const query = useQuery({
    queryKey: ['records', typeId, params],
    enabled: currentType !== undefined && currentEnv !== undefined,
    queryFn: async () => {
      const response = await recordsList(typeId, params);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос записей');
      }

      return response.data;
    },
    placeholderData: (previous) => previous,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['records'] });
    // Число записей показано в меню.
    void queryClient.invalidateQueries({ queryKey: ['content-types', siteId] });
  };

  const fail = (cause: unknown) => {
    toast.error('Не удалось выполнить действие', {
      description: cause instanceof ApiError ? cause.detail : 'Попробуйте ещё раз.',
    });
  };

  const act = (run: Promise<unknown>, done: string) => {
    run
      .then(() => {
        toast.success(done);
        refresh();
      })
      .catch(fail);
  };

  /** Изменить настройку на экране и вернуться к первой странице. */
  const tune = (patch: { [K in keyof ViewConfig]?: ViewConfig[K] | undefined }) => {
    setConfig((prev) => {
      const next: Record<string, unknown> = { ...prev };

      // Снятое — убирается из настройки, а не хранится как undefined:
      // иначе «вид изменён» загорался бы от одного щелчка туда-обратно.
      for (const [key, value] of Object.entries(patch)) {
        if (value === undefined) {
          delete next[key];
        } else {
          next[key] = value;
        }
      }

      return next as unknown as ViewConfig;
    });
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setSelection({});
  };

  const chooseView = (view: ViewBody | undefined) => {
    setViewID(view?.id);
    setConfig(view?.config ?? base);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setSelection({});
  };

  const selectedIDs = Object.keys(selection).filter((id) => selection[id] === true);

  // Shift-щелчок выбирает диапазон от прошлого щелчка. У флажка Radix в
  // onCheckedChange нет события — Shift запоминается в onClick, он раньше.
  const anchor = useRef<string | undefined>(undefined);
  const shift = useRef(false);
  const pick = (id: string, on: boolean) => {
    setSelection(
      (prev) =>
        selectRange(
          (query.data?.items ?? []).map((record) => record.id),
          prev,
          id,
          on,
          anchor.current,
          shift.current,
        ) as RowSelectionState,
    );
    anchor.current = id;
    shift.current = false;
  };
  const rangeClick = (event: { shiftKey: boolean }) => {
    shift.current = event.shiftKey;
  };
  // Столбцы таблицы мемоизированы — свежий выбор через ссылку.
  const pickRef = useRef(pick);

  useEffect(() => {
    pickRef.current = pick;
  });

  const bulk = (action: BulkAction) => {
    recordsBulk(typeId, { action, ids: selectedIDs })
      .then((response) => {
        if (response.status !== 200) {
          return;
        }

        const { done, failed } = response.data;
        const failures = failed ?? [];

        if ((done ?? []).length > 0) {
          toast.success(
            `${BULK_DONE[action]}: ${pluralize((done ?? []).length, ['запись', 'записи', 'записей'])}`,
          );
        }

        // Не прошедшие — с причиной: «у трёх не заполнен заголовок» — то,
        // что человек сможет поправить, а не «ошибка».
        if (failures.length > 0) {
          toast.warning(
            `Не вышло с ${pluralize(failures.length, ['записью', 'записями', 'записями'])}`,
            {
              description: failures
                .slice(0, 3)
                .map(
                  (item) =>
                    `«${item.title === '' ? 'без названия' : item.title}» — ${item.message}`,
                )
                .join('; '),
            },
          );
        }

        setSelection(Object.fromEntries(failures.map((item) => [item.id, true])));
        refresh();
      })
      .catch(fail);
  };

  const fieldColumns = useMemo(
    () =>
      (config.columns ?? [])
        .map((key) => fields.find((item) => item.key === key))
        .filter((item) => item !== undefined)
        .map((field) =>
          helper.display({
            id: `field:${field.key}`,
            header: field.name,
            enableSorting: sortable(field.type),
            meta: { name: field.name, align: field.type === 'number' ? 'end' : 'start' },
            cell: ({ row }) => (
              <span className={styles['cell__sub']}>
                {cellText(field, row.original.data[field.key])}
              </span>
            ),
          }),
        ),
    // fields пересобирается каждый рендер; столбцы зависят от схемы и выбора.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config.columns, schema.data],
  );

  const columns = useMemo(
    () =>
      helper.columns([
        helper.display({
          id: 'select',
          meta: { width: '40px', name: 'Выбор' },
          enableSorting: false,
          enableHiding: false,
          header: ({ table }) => (
            <Checkbox
              size="sm"
              aria-label="Выбрать все на странице"
              checked={
                table.getIsAllPageRowsSelected()
                  ? true
                  : table.getIsSomePageRowsSelected()
                    ? 'indeterminate'
                    : false
              }
              onCheckedChange={(value) => {
                table.toggleAllPageRowsSelected(value === true);
              }}
            />
          ),
          cell: ({ row }) => (
            <Checkbox
              size="sm"
              aria-label={`Выбрать «${row.original.title}»`}
              checked={row.getIsSelected()}
              onMouseDown={(event) => {
                // Иначе Shift-щелчок выделяет текст строк между флажками.
                if (event.shiftKey) event.preventDefault();
              }}
              onClick={(event) => {
                shift.current = event.shiftKey;
              }}
              onCheckedChange={(value) => {
                pickRef.current(row.original.id, value === true);
              }}
            />
          ),
        }),

        helper.accessor('title', {
          header: 'Запись',
          meta: { name: 'Запись' },
          cell: ({ row }) => (
            <Link
              to="/sites/$siteId/$env/r/$recordId"
              params={{ siteId, env, recordId: row.original.id }}
              className={styles['cell__link']}
            >
              {row.original.title === '' ? 'Без названия' : row.original.title}
            </Link>
          ),
        }),

        ...fieldColumns,

        helper.accessor('status', {
          header: 'Состояние',
          meta: { name: 'Состояние' },
          cell: ({ row }) => <StatusBadge record={row.original} />,
        }),

        helper.accessor('updated_at', {
          header: 'Изменена',
          meta: { align: 'end', name: 'Изменена' },
          cell: ({ getValue }) => (
            <span className={styles['cell__sub']}>{dateFormat.format(new Date(getValue()))}</span>
          ),
        }),

        helper.display({
          id: 'actions',
          meta: { align: 'end', width: '56px', name: 'Действия' },
          enableHiding: false,
          header: () => <span className={styles['hidden-label']}>Действия</span>,
          cell: ({ row }) => (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  iconOnly
                  variant="ghost"
                  tone="neutral"
                  size="sm"
                  aria-label={`Действия с записью «${row.original.title}»`}
                >
                  <Ellipsis size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {row.original.deleted_at != null ? (
                  <DropdownMenuItem
                    icon={<RotateCcw size={15} />}
                    onSelect={() => {
                      act(recordsRestore(row.original.id), 'Запись восстановлена');
                    }}
                  >
                    Восстановить
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    icon={<Trash2 size={15} />}
                    tone="danger"
                    onSelect={() => {
                      act(recordsDelete(row.original.id), 'Запись в корзине');
                    }}
                  >
                    В корзину
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ),
        }),
      ]),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- act пересоздаётся каждый рендер
    [fieldColumns],
  );

  const records = query.data?.items ?? NO_RECORDS;

  const table = useDataTable({
    columns,
    data: records,
    getRowId: (record) => record.id,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    enableRowSelection: canPublish,
    rowCount: query.data?.total ?? 0,
    state: {
      sorting,
      pagination,
      rowSelection: selection,
      columnVisibility: { actions: canPublish, select: canPublish },
    },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const first = next[0];

      tune(
        first === undefined
          ? { sort: undefined, order: undefined }
          : { sort: first.id, order: first.desc ? 'desc' : 'asc' },
      );
    },
    onPaginationChange: setPagination,
    onRowSelectionChange: setSelection,
  });

  const saveView = (name: string) => {
    recordsViewCreate(typeId, { name: name.trim(), config })
      .then((response) => {
        if (response.status === 201) {
          toast.success(`Вид «${name.trim()}» сохранён`);
          void queryClient.invalidateQueries({ queryKey: ['views', typeId] });
          setViewID(response.data.id);
        }
      })
      .catch(fail);
  };

  const updateView = () => {
    if (currentView === undefined) {
      return;
    }

    recordsViewUpdate(currentView.id, { config }, { 'If-Match': currentView.etag })
      .then(() => {
        toast.success('Вид обновлён');
        void queryClient.invalidateQueries({ queryKey: ['views', typeId] });
      })
      .catch(fail);
  };

  const removeView = (view: ViewBody) => {
    recordsViewDelete(view.id)
      .then(() => {
        toast.success(`Вид «${view.name}» удалён`);
        void queryClient.invalidateQueries({ queryKey: ['views', typeId] });

        if (view.id === viewID) {
          chooseView(undefined);
        }
      })
      .catch(fail);
  };

  return (
    <div className={styles['page']}>
      {!embedded && (
        <header className={styles['head']}>
          <div>
            <h1 className={styles['title']}>{currentType?.name ?? ''}</h1>
            {/* Описание коллекции из схемы: схема сама становится подсказкой
              менеджеру, что здесь лежит и как с этим обращаться. */}
            {currentType !== undefined && currentType.description !== '' && (
              <p className={styles['muted']}>{currentType.description}</p>
            )}
          </div>
        </header>
      )}

      {scope === null || scope.loading ? null : currentType === undefined ? (
        <p className={styles['muted']}>Такой коллекции у сайта нет — возможно, её удалили.</p>
      ) : currentEnv === undefined ? (
        <p className={styles['muted']}>У сайта нет этого окружения.</p>
      ) : ordering ? (
        <>
          <div className={styles['toolbar']}>
            <span className={styles['muted']}>
              Перетащите записи в нужном порядке — так их отдаст сайт.
            </span>
            <Button
              className={styles['toolbar__end']}
              iconLeft={<ArrowDownUp size={15} />}
              onClick={() => {
                setOrdering(false);
              }}
            >
              Готово
            </Button>
          </div>
          <OrderList typeID={currentType.id} />
        </>
      ) : (
        <>
          {((views.data ?? []).length > 0 || (canWrite && changed)) && (
            <nav className={styles['views']} aria-label="Сохранённые виды">
              <button
                type="button"
                className={`${styles['view'] ?? ''} ${viewID === undefined ? (styles['view--active'] ?? '') : ''}`}
                onClick={() => {
                  chooseView(undefined);
                }}
              >
                Все записи
              </button>

              {(views.data ?? []).map((view) => (
                <span key={view.id} className={styles['view__wrap']}>
                  <button
                    type="button"
                    className={`${styles['view'] ?? ''} ${view.id === viewID ? (styles['view--active'] ?? '') : ''}`}
                    onClick={() => {
                      chooseView(view);
                    }}
                  >
                    {view.name}
                  </button>
                  {view.id === viewID && canWrite && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          iconOnly
                          size="sm"
                          variant="ghost"
                          tone="neutral"
                          aria-label={`Вид «${view.name}»`}
                        >
                          <Ellipsis size={14} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        <DropdownMenuItem
                          icon={<Trash2 size={15} />}
                          tone="danger"
                          onSelect={() => {
                            removeView(view);
                          }}
                        >
                          Удалить вид
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </span>
              ))}

              {canWrite && changed && (
                <span className={styles['views__save']}>
                  {currentView !== undefined && (
                    <Button size="sm" variant="ghost" tone="neutral" onClick={updateView}>
                      Обновить «{currentView.name}»
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    iconLeft={<Plus size={14} />}
                    onClick={() => {
                      setNaming(true);
                    }}
                  >
                    Сохранить вид
                  </Button>
                </span>
              )}
            </nav>
          )}

          {selectedIDs.length > 0 ? (
            <DataTableSelection
              count={selectedIDs.length}
              onClear={() => {
                setSelection({});
              }}
            >
              {deleted ? (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    tone="neutral"
                    iconLeft={<RotateCcw size={14} />}
                    onClick={() => {
                      bulk('restore');
                    }}
                  >
                    Вернуть
                  </Button>
                  {env === 'dev' && canWrite && scope?.devOnly !== true && (
                    <Button
                      size="sm"
                      variant="outline"
                      tone="danger"
                      iconLeft={<ArrowUpRight size={14} />}
                      onClick={() => {
                        setPromoting(selectedIDs);
                      }}
                    >
                      Удалить и на Production
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <Button
                    size="sm"
                    iconLeft={<Send size={14} />}
                    onClick={() => {
                      bulk('publish');
                    }}
                  >
                    Опубликовать
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    tone="neutral"
                    iconLeft={<CloudOff size={14} />}
                    onClick={() => {
                      bulk('unpublish');
                    }}
                  >
                    Снять с сайта
                  </Button>
                  {env === 'dev' && canWrite && scope?.devOnly !== true && (
                    <Button
                      size="sm"
                      variant="outline"
                      tone="neutral"
                      iconLeft={<ArrowUpRight size={14} />}
                      onClick={() => {
                        setPromoting(selectedIDs);
                      }}
                    >
                      На Production
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    tone="danger"
                    iconLeft={<Trash2 size={14} />}
                    onClick={() => {
                      setConfirmDelete(true);
                    }}
                  >
                    В корзину
                  </Button>
                </>
              )}
            </DataTableSelection>
          ) : (
            <div className={styles['toolbar']}>
              <Input
                className={styles['toolbar__search']}
                placeholder="Поиск по названию"
                aria-label="Поиск по записям"
                prefix={<Search size={15} />}
                clearable
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
              />

              <Segmented
                value={config.status ?? 'all'}
                aria-label="Состояние"
                onValueChange={(value) => {
                  tune({ status: value === 'all' ? undefined : (value as ViewConfig['status']) });
                }}
                options={[
                  { value: 'all', label: 'Все' },
                  { value: 'published', label: 'На сайте' },
                  { value: 'draft', label: 'Черновики' },
                ]}
              />

              <div className={styles['toolbar__end']}>
                <FiltersPanel
                  className={styles['only-narrow']}
                  fields={fields}
                  filters={config.filters ?? []}
                  total={query.data?.total}
                  onChange={(filters) => {
                    tune({ filters });
                  }}
                />

                <Segmented
                  value={config.layout}
                  aria-label="Как показывать"
                  onValueChange={(value) => {
                    const next = value as ViewConfig['layout'];
                    tune({ layout: next });
                    setLayout(next);
                    storeLayout(typeId, next);
                  }}
                  options={[
                    { value: 'table', label: 'Таблица' },
                    { value: 'cards', label: 'Карточки' },
                    ...(hasStages(currentType.workflow)
                      ? [{ value: 'kanban', label: 'Канбан' }]
                      : []),
                  ]}
                />

                {config.layout === 'table' && fields.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        iconOnly
                        variant="outline"
                        tone="neutral"
                        aria-label="Столбцы таблицы"
                      >
                        <Columns3 size={15} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Поля в таблице</DropdownMenuLabel>
                      {fields.map((field) => {
                        const on = (config.columns ?? []).includes(field.key);

                        return (
                          <DropdownMenuCheckboxItem
                            key={field.key}
                            checked={on}
                            onSelect={(event) => {
                              event.preventDefault();
                            }}
                            onCheckedChange={() => {
                              tune({
                                columns: on
                                  ? (config.columns ?? []).filter((key) => key !== field.key)
                                  : [...(config.columns ?? []), field.key],
                              });
                            }}
                          >
                            {field.name}
                          </DropdownMenuCheckboxItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                <Button
                  iconOnly
                  variant={deleted ? 'solid' : 'outline'}
                  tone="neutral"
                  aria-label={deleted ? 'Выйти из корзины' : 'Корзина'}
                  aria-pressed={deleted}
                  title="Корзина"
                  onClick={() => {
                    setDeleted((value) => !value);
                    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    setSelection({});
                  }}
                >
                  <Trash2 size={15} />
                </Button>

                {manual && canPublish && (
                  <Button
                    variant="outline"
                    tone="neutral"
                    iconLeft={<ArrowDownUp size={15} />}
                    onClick={() => {
                      setOrdering(true);
                    }}
                  >
                    Расставить
                  </Button>
                )}

                {canWrite && (
                  <Button
                    iconLeft={<Plus size={15} />}
                    onClick={() => {
                      setCreating(true);
                    }}
                  >
                    Создать запись
                  </Button>
                )}
              </div>
            </div>
          )}

          <div className={styles['layout']}>
            <div className={styles['layout__main']}>
              {config.layout === 'kanban' && hasStages(currentType.workflow) && !deleted ? (
                <Kanban
                  type={currentType}
                  onOpen={(record) => {
                    void navigate({
                      to: '/sites/$siteId/$env/r/$recordId',
                      params: { siteId, env, recordId: record.id },
                    });
                  }}
                />
              ) : config.layout === 'cards' ? (
                <RecordCards
                  records={records}
                  fields={fields}
                  loading={query.isLoading}
                  selectable={canPublish}
                  selection={selection}
                  onToggle={pick}
                  onRangeClick={rangeClick}
                  link={(record) => ({ siteId, env, recordId: record.id })}
                />
              ) : (
                <DataTable
                  table={table}
                  caption={`Записи «${currentType.name}»`}
                  loading={query.isLoading}
                  refreshing={query.isFetching && !query.isLoading}
                  empty={
                    <DataTableEmpty
                      icon={<Search size={22} />}
                      title={deleted ? 'Корзина пуста' : 'Записей не нашлось'}
                      description={
                        deleted
                          ? 'Удалённые записи появятся здесь.'
                          : (config.filters ?? []).length > 0 ||
                              search !== '' ||
                              config.status !== undefined
                            ? 'Под эти условия ничего не подходит.'
                            : `Создайте первую запись «${currentType.name}».`
                      }
                    />
                  }
                />
              )}

              {config.layout !== 'kanban' && <DataTablePagination table={table} />}
            </div>

            <div className={styles['layout__side']}>
              <FiltersSide
                fields={fields}
                filters={config.filters ?? []}
                onChange={(filters) => {
                  tune({ filters });
                }}
              />
            </div>
          </div>
        </>
      )}

      {currentType !== undefined && currentEnv !== undefined && (
        <NewRecordDialog
          open={creating}
          onOpenChange={setCreating}
          typeID={currentType.id}
          onDone={(record) => {
            refresh();
            void navigate({
              to: '/sites/$siteId/$env/r/$recordId',
              params: { siteId, env, recordId: record.id },
            });
          }}
        />
      )}

      <NameViewDialog open={naming} onOpenChange={setNaming} onSave={saveView} />

      <PromoteDialog
        siteID={siteId}
        recordIDs={promoting}
        onClose={() => {
          setPromoting([]);
        }}
        onDone={() => {
          setSelection({});
        }}
      />

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={`Убрать в корзину ${pluralize(selectedIDs.length, ['запись', 'записи', 'записей'])}?`}
        description={`Опубликованные пропадут с сайта${
          scope?.env === 'prod' ? ' Production' : ''
        }. Вернуть можно из корзины.`}
        confirmLabel="В корзину"
        tone="danger"
        onConfirm={() => {
          bulk('delete');
        }}
      />
    </div>
  );
}

const layoutKey = (typeId: string) => `faceta:layout:${typeId}`;
const LAYOUTS: readonly string[] = ['table', 'cards', 'kanban'];

/** Запомненный вид модели; в приватном окне или впервые — таблица. */
function storedLayout(typeId: string): ViewConfig['layout'] {
  try {
    const value = localStorage.getItem(layoutKey(typeId));

    return value !== null && LAYOUTS.includes(value)
      ? (value as ViewConfig['layout'])
      : DEFAULT_CONFIG.layout;
  } catch {
    return DEFAULT_CONFIG.layout;
  }
}

function storeLayout(typeId: string, layout: ViewConfig['layout']) {
  try {
    localStorage.setItem(layoutKey(typeId), layout);
  } catch {
    // Приватное окно — вид не запомнится, это не ошибка.
  }
}

/** Условия без значения ещё не дописаны — в запрос они не уходят. */
function completeFilters(filters: FieldFilter[]): FieldFilter[] {
  return filters.filter(
    (item) =>
      item.op === 'filled' ||
      item.op === 'empty' ||
      (item.value !== undefined && item.value !== null && item.value !== ''),
  );
}

/**
 * Карточки: обложка из первого поля-картинки, название, состояние и пара
 * полей. Для коллекций, где важнее видеть, чем сравнивать, — сотрудники,
 * портфолио, товары.
 */
function RecordCards({
  records,
  fields,
  loading,
  selectable,
  selection,
  onToggle,
  onRangeClick,
  link,
}: {
  records: RecordBody[];
  fields: ReturnType<typeof listFields>;
  loading: boolean;
  selectable: boolean;
  selection: RowSelectionState;
  onToggle: (id: string, on: boolean) => void;
  /** Щелчок по флажку — до onToggle: с Shift выбирается диапазон. */
  onRangeClick: (event: { shiftKey: boolean }) => void;
  link: (record: RecordBody) => { siteId: string; env: EnvKey; recordId: string };
}) {
  const image = fields.find((field) => field.type === 'image');
  const extra = fields
    .filter((field) => ['string', 'select', 'number', 'date', 'boolean'].includes(field.type))
    .filter((field) => field.key !== 'title')
    .slice(0, 2);

  if (loading) {
    return <p className={styles['muted']}>Загружаем…</p>;
  }

  if (records.length === 0) {
    return <p className={styles['muted']}>Записей не нашлось.</p>;
  }

  return (
    <ul className={styles['cards']}>
      {records.map((record) => {
        const cover = image === undefined ? undefined : record.data[image.key];

        return (
          <li key={record.id} className={styles['card']}>
            {selectable && (
              <span className={styles['card__check']}>
                <Checkbox
                  size="sm"
                  aria-label={`Выбрать «${record.title}»`}
                  checked={selection[record.id] === true}
                  onMouseDown={(event) => {
                    if (event.shiftKey) event.preventDefault();
                  }}
                  onClick={onRangeClick}
                  onCheckedChange={(value) => {
                    onToggle(record.id, value === true);
                  }}
                />
              </span>
            )}

            <Link
              to="/sites/$siteId/$env/r/$recordId"
              params={link(record)}
              className={styles['card__link']}
            >
              <span className={styles['card__cover']}>
                {typeof cover === 'string' && cover !== '' ? (
                  <AuthImage
                    src={`/media/${cover}/thumb`}
                    alt=""
                    className={styles['card__image']}
                  />
                ) : (
                  <LayoutGrid size={22} aria-hidden="true" />
                )}
              </span>
              <span className={styles['card__body']}>
                <span className={styles['card__title']}>
                  {record.title === '' ? 'Без названия' : record.title}
                </span>
                {extra.map((field) => (
                  <span key={field.key} className={styles['card__meta']}>
                    {field.name}: {cellText(field, record.data[field.key])}
                  </span>
                ))}
                <StatusBadge record={record} />
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function StatusBadge({ record }: { record: RecordBody }) {
  if (record.deleted_at != null) {
    return (
      <Badge size="sm" tone="neutral">
        в корзине
      </Badge>
    );
  }

  if (record.status !== 'published') {
    return (
      <Badge size="sm" tone="neutral">
        черновик
      </Badge>
    );
  }

  // Опубликована, но с тех пор правилась: на сайте не то, что в редакторе.
  if (record.has_unpublished_changes) {
    return (
      <Badge size="sm" tone="warning">
        есть правки
      </Badge>
    );
  }

  return (
    <Badge size="sm" tone="success">
      на сайте
    </Badge>
  );
}

/** Название нового вида: «Чай», «На проверке», «Опубликованные за месяц». */
function NameViewDialog({
  open,
  onOpenChange,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (name: string) => void;
}) {
  const [name, setName] = useState('');

  const submit = (event: FormEvent) => {
    event.preventDefault();

    if (name.trim() === '') {
      return;
    }

    onSave(name);
    setName('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="Сохранить вид"
        description="Вид запоминает, как показывать список и что в нём отбирать. Он виден всей команде."
        size="sm"
        footer={
          <Button type="submit" form="view-name" disabled={name.trim() === ''}>
            Сохранить
          </Button>
        }
      >
        <form id="view-name" onSubmit={submit}>
          <Field label="Название" required>
            <Input
              value={name}
              autoFocus
              maxLength={60}
              placeholder="Например, «На проверке»"
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
          </Field>
        </form>
      </DialogContent>
    </Dialog>
  );
}
